import { AppConfig } from '../../../core/app.config';
import { BACKGROUND_NETWORK_REQ_OPTIONS } from '../../../core/constants/constants';
import { courier } from '../../../core/services/courier.service';
import { createHttpParams } from '../../../core/utils/request';
import {
  CreateVPCConnectionPayload,
  EditVPCConnectionPayload,
  VPCPeeringConnection
} from '../Connectivity/interface';
import {
  CreateSnowflakePrivateLinkPayload,
  SnowflakeAuthorization,
  SnowflakePrivateLinkConnection,
  SnowflakePrivateLinkCreateResponse
} from '../SnowflakePrivateLink/interface';

const appConfig = new AppConfig();

export interface ConnectivityActionResponse {
  message?: string;
}

export interface PaginatedConnectivityResponse<T> {
  total_count: number;
  data: T[];
}

export type VPCPeeringListResponse = PaginatedConnectivityResponse<VPCPeeringConnection>;

export type SnowflakePrivateLinkListResponse = PaginatedConnectivityResponse<SnowflakePrivateLinkConnection>;

const ConnectivityAPI = {
  connectivityURL: `${appConfig.getConnectivityServiceURL()}`,

  getConnectivityPeeringList(teamId: number, hevoRegion?: string): Promise<VPCPeeringListResponse> {
    return courier.get(`${this.connectivityURL}/peering/requests`, {
      ...BACKGROUND_NETWORK_REQ_OPTIONS,
      networkOptions: {
        params: createHttpParams({ team_id: teamId, hevo_region: hevoRegion ?? null })
      }
    })
  },

  createPeeringRequest(payload: CreateVPCConnectionPayload): Promise<ConnectivityActionResponse> {
    return courier.post(`${this.connectivityURL}/peering/requests`, BACKGROUND_NETWORK_REQ_OPTIONS, payload)
  },

  editPeeringRequest(connectionId: string, payload: EditVPCConnectionPayload): Promise<ConnectivityActionResponse> {
    return courier.patch(`${this.connectivityURL}/peering/requests/${connectionId}`, BACKGROUND_NETWORK_REQ_OPTIONS, payload)
  },

  // Takes the same body as edit, so a correction and the retry are one call
  // rather than a save that might never be followed by a retry. An empty body
  // is a plain retry.
  resubmitPeeringRequest(connectionId: string, payload?: EditVPCConnectionPayload): Promise<ConnectivityActionResponse> {
    return courier.post(`${this.connectivityURL}/peering/requests/${connectionId}/resubmit`, BACKGROUND_NETWORK_REQ_OPTIONS, payload ?? {})
  },

  deletePeeringRequest(connectionId: string): Promise<ConnectivityActionResponse> {
    return courier.delete(`${this.connectivityURL}/peering/requests/${connectionId}`, BACKGROUND_NETWORK_REQ_OPTIONS)
  },

  getSnowflakePrivateLinkList(teamId: number, hevoRegion?: string): Promise<SnowflakePrivateLinkListResponse> {
    return courier.get(`${this.connectivityURL}/snowflake-privatelink/requests`, {
      ...BACKGROUND_NETWORK_REQ_OPTIONS,
      networkOptions: {
        params: createHttpParams({ team_id: teamId, hevo_region: hevoRegion ?? null })
      }
    })
  },

  createSnowflakePrivateLinkRequest(payload: CreateSnowflakePrivateLinkPayload): Promise<SnowflakePrivateLinkCreateResponse> {
    return courier.post(`${this.connectivityURL}/snowflake-privatelink/requests`, BACKGROUND_NETWORK_REQ_OPTIONS, payload)
  },

  resubmitSnowflakePrivateLinkRequest(connectionId: string): Promise<ConnectivityActionResponse> {
    return courier.post(`${this.connectivityURL}/snowflake-privatelink/requests/${connectionId}/resubmit`, BACKGROUND_NETWORK_REQ_OPTIONS, {})
  },

  reissueSnowflakePrivateLinkToken(connectionId: string): Promise<SnowflakeAuthorization> {
    return courier.post(`${this.connectivityURL}/snowflake-privatelink/requests/${connectionId}/authorization-token`, BACKGROUND_NETWORK_REQ_OPTIONS, {})
  },

  confirmSnowflakePrivateLinkAuthorized(connectionId: string): Promise<ConnectivityActionResponse> {
    return courier.post(`${this.connectivityURL}/snowflake-privatelink/requests/${connectionId}/authorized`, BACKGROUND_NETWORK_REQ_OPTIONS, {})
  }
};

export default ConnectivityAPI;
